import { useEffect } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { DEFAULT_PALETTE, themeCustomProperties } from "@/lib/theme-tokens";

// Workshop's runtime palette customization. The store holds the user-
// pickable values (background, surface, accent); a separate
// useApplyThemePrefs hook pushes them into CSS custom properties on
// document.documentElement, along with auto-contrast ink colours per surface.
//
// Ported from workshop/shell/ThemePrefs.jsx - same defaults, same
// localStorage keys + palette migration version, exposed via a hook with
// the same shape (useThemePrefs) so Settings.tsx is a direct port.

export const DEFAULT_BG = DEFAULT_PALETTE.page;
export const DEFAULT_ACCENT = DEFAULT_PALETTE.accent;
export const DEFAULT_SURFACE = DEFAULT_PALETTE.surface;

const PALETTE_VERSION = "4";
const STORAGE_KEY = "smycles:theme-prefs";

interface ThemePrefsState {
  menuPosition: "top" | "left";
  bgColor: string;
  accentColor: string;
  surfaceColor: string;
  paletteVersion: string;

  setMenuPosition: (v: "top" | "left") => void;
  setBgColor: (v: string) => void;
  setAccentColor: (v: string) => void;
  setSurfaceColor: (v: string) => void;
  resetPalette: () => void;
}

const noopStorage: Storage = {
  length: 0,
  clear: () => undefined,
  getItem: () => null,
  key: () => null,
  removeItem: () => undefined,
  setItem: () => undefined,
};

function resolveStorage(): Storage {
  try {
    if (typeof window === "undefined") return noopStorage;
    const candidate = window.localStorage;
    if (!candidate || typeof candidate.setItem !== "function") return noopStorage;
    return candidate;
  } catch {
    return noopStorage;
  }
}

export const useThemePrefs = create<ThemePrefsState>()(
  persist(
    (set) => ({
      menuPosition: "top",
      bgColor: DEFAULT_BG,
      accentColor: DEFAULT_ACCENT,
      surfaceColor: DEFAULT_SURFACE,
      paletteVersion: PALETTE_VERSION,

      setMenuPosition: (menuPosition) => set({ menuPosition }),
      setBgColor: (bgColor) => set({ bgColor }),
      setAccentColor: (accentColor) => set({ accentColor }),
      setSurfaceColor: (surfaceColor) => set({ surfaceColor }),
      resetPalette: () =>
        set({
          bgColor: DEFAULT_BG,
          accentColor: DEFAULT_ACCENT,
          surfaceColor: DEFAULT_SURFACE,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => resolveStorage()),
      // Migration: if the stored palette is from an earlier version of the
      // default palette, drop the persisted colours so the new defaults
      // take effect. Bump PALETTE_VERSION whenever the defaults change.
      migrate: (persisted, _version) => {
        const p = persisted as Partial<ThemePrefsState> | undefined;
        if (!p || p.paletteVersion !== PALETTE_VERSION) {
          return {
            menuPosition: p?.menuPosition ?? "top",
            bgColor: DEFAULT_BG,
            accentColor: DEFAULT_ACCENT,
            surfaceColor: DEFAULT_SURFACE,
            paletteVersion: PALETTE_VERSION,
          } as ThemePrefsState;
        }
        return persisted as ThemePrefsState;
      },
      version: 1,
    },
  ),
);

// Mount this hook once at the top of AppShell (or wherever the app root
// lives). It subscribes to the palette state and pushes the derived
// CSS custom properties to :root so every utility class picks them up.
export function useApplyThemePrefs(): void {
  const bgColor = useThemePrefs((s) => s.bgColor);
  const accentColor = useThemePrefs((s) => s.accentColor);
  const surfaceColor = useThemePrefs((s) => s.surfaceColor);

  useEffect(() => {
    const root = document.documentElement;
    const props = themeCustomProperties({ page: bgColor, surface: surfaceColor, accent: accentColor });
    for (const [name, value] of Object.entries(props)) root.style.setProperty(name, value);
  }, [bgColor, accentColor, surfaceColor]);
}
